import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Pause, Play } from "lucide-react";
import { type PointerEvent, useState } from "react";

import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";

import { formatGameTime } from "../../format/formatGameTime";
import type { TimelineEvent } from "../../map/TimelineEvent";
import { nearestEvent } from "../../map/timelineEvents";

const PLAY = msg({ id: "analysis.map.play", message: "Play" });
const PAUSE = msg({ id: "analysis.map.pause", message: "Pause" });
const SPEED = msg({ id: "analysis.map.speed", message: "Playback speed: {speed}×" });
const SPEED_VALUE = msg({ id: "analysis.map.speed_value", message: "{speed}×" });
const MOMENT = msg({ id: "analysis.map.moment", message: "Moment of the match" });
const HP = msg({ id: "analysis.map.hp", message: "HP {hp}%" });
const DEAD = msg({ id: "analysis.map.dead", message: "dead" });

// Событие под курсором ищем не дальше 15 с и не дальше 10 пикселей шкалы.
const SNAP_S = 15;
const SNAP_PX = 10;

const TICKS: Record<TimelineEvent["kind"], string> = {
  death: cn("top-0.5 h-2 w-0.5 rounded-sm bg-map-missed"),
  kill: cn("bottom-0.5 h-2 w-0.5 rounded-sm bg-map-kill"),
  boss: cn("top-0 size-2 rounded-full border border-map-ink"),
  objective: cn("bottom-0 size-1.5 rotate-45 border border-map-ink"),
  rune: cn("bottom-0 size-1.5 rounded-full bg-map-kill"),
};

type TimeBarProps = {
  t: number;
  durationS: number;
  playing: boolean;
  speed: number;
  // HP игрока в этот момент; `null` — мёртв или нет данных.
  hp: number | null;
  dead: boolean;
  // Своя команда: цвет отметок мид-босса и зданий — по команде.
  team: number;
  events: readonly TimelineEvent[];
  // Событие, которое сейчас в карточке на карте: его отметка крупнее.
  shown: TimelineEvent | null;
  onToggle: () => void;
  onSpeed: () => void;
  onSeek: (t: number) => void;
  // Отпустили шкалу рядом с отметкой — переход к событию.
  onJump: (event: TimelineEvent) => void;
  // Курсор над шкалой: ближайшее событие или `null`.
  onHover: (event: TimelineEvent | null) => void;
};

// Время матча: проигрывание и скорость, ползунок с отметками событий, HP игрока.
// Курсор над шкалой показывает время и ближайшее событие, отпущенная рядом с отметкой
// шкала переходит к событию. Время и HP постоянной ширины — шкала не прыгает.
export const TimeBar = ({
  t,
  durationS,
  playing,
  speed,
  hp,
  dead,
  team,
  events,
  shown,
  onToggle,
  onSpeed,
  onSeek,
  onJump,
  onHover,
}: TimeBarProps) => {
  const { _ } = useLingui();
  // Указатель над шкалой: где, какой момент и есть ли рядом событие.
  const [hoverX, setHoverX] = useState<{ x: number; t: number; near: boolean } | null>(null);
  const teamColor = (ours: boolean) =>
    (team === 0) === ours ? "bg-team-amber" : "bg-team-sapphire";
  // Момент под указателем и ближайшее к нему событие.
  const pointed = (event: PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) {
      return null;
    }
    const x = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
    const at = (x / rect.width) * durationS;
    const window = Math.max(SNAP_S, (SNAP_PX / rect.width) * durationS);
    return { x, at, event: nearestEvent(events, at, window) };
  };
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <div className="flex gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={_(playing ? PAUSE : PLAY)}
          onClick={onToggle}
        >
          {playing ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
        </Button>
        <Button
          type="button"
          variant="outline"
          aria-label={_({ ...SPEED, values: { speed } })}
          title={_({ ...SPEED, values: { speed } })}
          className="w-12 px-0 font-mono text-[13px]"
          onClick={onSpeed}
        >
          {_({ ...SPEED_VALUE, values: { speed } })}
        </Button>
      </div>
      <span className="w-[13ch] shrink-0 font-mono text-[15px] tabular-nums">
        {formatGameTime(t)} / {formatGameTime(durationS)}
      </span>
      <span className="ml-auto w-[8ch] shrink-0 font-mono text-[13px] text-muted @lg:order-last @lg:ml-0">
        {dead ? _(DEAD) : hp === null ? "—" : _({ ...HP, values: { hp } })}
      </span>
      <div
        className="relative flex h-9 min-w-0 basis-full items-center @lg:flex-[1_1_260px]"
        onPointerMove={(event) => {
          const point = pointed(event);
          if (point === null) {
            return;
          }
          setHoverX({ x: point.x, t: point.at, near: point.event !== null });
          onHover(point.event);
        }}
        onPointerLeave={() => {
          setHoverX(null);
          onHover(null);
        }}
        onPointerUp={(event) => {
          const target = pointed(event)?.event ?? null;
          if (target !== null) {
            onJump(target);
          }
        }}
      >
        <input
          type="range"
          min={0}
          max={durationS}
          step={1}
          value={Math.round(t)}
          aria-label={_(MOMENT)}
          aria-valuetext={formatGameTime(t)}
          onChange={(event) => {
            onSeek(Number(event.target.value));
          }}
          className="w-full accent-accent"
        />
        {events.map((event, index) => (
          <span
            // События матча не меняются, пока открыта карта.
            key={index}
            aria-hidden="true"
            data-tick={event.kind}
            style={{ left: `${((event.at / durationS) * 100).toFixed(2)}%` }}
            className={cn(
              "pointer-events-none absolute -translate-x-1/2 transition-transform",
              TICKS[event.kind],
              (event.kind === "boss" || event.kind === "objective") && teamColor(event.ours),
              event === shown && "scale-150",
            )}
          />
        ))}
        {hoverX !== null && !hoverX.near && (
          <span
            aria-hidden="true"
            style={{ left: hoverX.x }}
            className="pointer-events-none absolute -top-6 -translate-x-1/2 rounded bg-surface-raised px-1.5 py-0.5 font-mono text-[11px] text-foreground-soft"
          >
            {formatGameTime(hoverX.t)}
          </span>
        )}
      </div>
    </div>
  );
};
